/**
 * O que acontece entre o encerramento e a avaliação, do lado do cliente.
 *
 * ## O pedido
 *
 * Ponto 3 da Gisele (20/10/2026): "quando o tempo acabar, não fechar a
 * conversa na hora. O cliente deve conseguir ver o histórico antes da
 * avaliação, para entender por que o atendimento foi encerrado."
 *
 * O caso dela é o encerramento por INATIVIDADE: o cliente estava longe da
 * tela, volta, e já está numa tela de notas sem ter lido nada -- nem a
 * mensagem que diz por que a conversa acabou.
 *
 * ## O que NÃO muda
 *
 * A avaliação continua abrindo SOZINHA. Isso foi pedido por ela mesma em
 * 15/10: antes era preciso clicar, e muita gente desistia ali, deixando o
 * atendimento sem nota. Tirar o automático de volta seria desfazer aquilo.
 *
 * O que entra é uma saída: enquanto a contagem corre, o cliente pode dizer
 * "quero ler". A partir daí nada o tira da conversa até ele mesmo pedir.
 *
 * ## Por que isto é um módulo
 *
 * `ChatMessages` monta socket, upload e áudio -- não há teste de componente
 * neste repositório, só de util. Estas quatro decisões são a parte que pode
 * errar em silêncio (contar para sempre, reagendar por cima de quem está
 * lendo, prometer "em instantes" quando a contagem parou), e aqui elas têm
 * teste.
 */

/**
 * Quanto tempo a conversa fica na tela antes de a avaliação aparecer.
 *
 * Eram 3 segundos. Oito não resolvem o pedido sozinhos -- quem resolve é
 * parar a contagem --, eles só dão tempo de ler a frase e alcançar o botão.
 *
 * O número não foi medido: é o que cabe entre "deu para reagir" e "a tela
 * parece travada".
 */
export const SEGUNDOS_ATE_A_AVALIACAO = 8;

/**
 * Agenda a troca de tela?
 *
 * @param {boolean} jaAgendado  já há um `setTimeout` pendurado
 * @param {boolean} lendo       o cliente pediu para ler a conversa
 */
export const deveAgendarAAvaliacao = (jaAgendado, lendo) => !jaAgendado && !lendo;

/**
 * O texto da tarja.
 *
 * Lendo, ele NÃO pode prometer que a avaliação abre: ela não vai abrir
 * sozinha, e uma tarja dizendo o contrário faria o cliente esperar por algo
 * que não vem.
 */
export const textoDaTarja = (lendo, segundos) => {
    if (lendo) {
        return 'Conversa encerrada. Leia com calma -- a avaliação só abre quando você quiser.';
    }

    // Nunca negativo: o relógio da tela e o `setTimeout` são dois processos, e
    // uma aba em segundo plano atrasa o primeiro. "abre em -2s" é o que
    // apareceria.
    const restante = Math.max(Number(segundos) || 0, 0);

    return `Conversa encerrada. A avaliação abre em ${restante}s.`;
};

/** O rótulo do botão que abre a avaliação. */
export const rotuloDoBotaoDeAvaliar = (lendo) => (lendo ? 'Avaliar atendimento' : 'Avaliar agora');

/**
 * Mostra o botão "Ler a conversa"?
 *
 * Só enquanto há contagem para parar. Depois de clicado não há o que parar, e
 * um botão que não faz nada é pior que nenhum.
 */
export const mostrarBotaoDeLer = (lendo) => !lendo;
